"use client"

import { useInViewport } from "ahooks"
import { useEffect, useRef } from "react"
import { StorefrontCart, StorefrontCartItem, StorefrontOrder } from "src/components/checkout/types"
import { useEventCallback } from "src/components/hooks/useEventCallback"
import { useInViewEffectOncePerPage } from "src/components/hooks/useInViewEffectOncePerPage"
import { useWindowEvent } from "src/components/hooks/useWindowEvent"
import { useWindowLocationPathname } from "src/components/hooks/useWindowLocationPathname"
import { ResolvedProductData } from "src/components/product-details/useResolvedProductData"
import { ResolvedListProductData } from "src/data/product-list-resolved/types"
import { WCStoreCreateOrderResponseData } from "src/data/woocommerce/wcStoreTypes"
import { Ccm19Event } from "src/integrations/ccm19/constants"
import { hasUserInteractedWithConsent } from "src/integrations/ccm19/useConsented"
import {
  googleTrackAddPaymentInfo,
  googleTrackAddShippingInfo,
  googleTrackAddToCart,
  googleTrackBeginCheckout,
  googleTrackEmbedView,
  googleTrackGenerateLead,
  googleTrackPageView,
  googleTrackPurchase,
  googleTrackRemoveFromCart,
  googleTrackSearch,
  googleTrackSelectItem,
  googleTrackSelectPromotion,
  googleTrackViewCart,
  googleTrackViewItem,
  googleTrackViewItemList,
  googleTrackViewPromotion,
} from "src/integrations/google/api"
import { dataLayerPushEvent } from "src/integrations/google/dataLayer"
import {
  mapCartOrderToGoogleAddPaymentInfoEvent,
  mapCartToGoogleAddShippingInfoEvent,
  mapCartToGoogleBeginCheckoutEvent,
  mapCartToGoogleViewCartEvent,
  mapLineItemToGoogleAddToCartEvent,
  mapOrderToGooglePurchaseEvent,
  mapProductToGoogleItemData,
  mapProductToGoogleRemoveFromCartEvent,
  mapProductToGoogleViewItemEvent,
  mapProductToGoogleViewPromotionItemData,
} from "src/integrations/google/mapping"
import { klarTrackCartEvent, klarTrackCheckoutEvent, klarTrackOrderEvent } from "src/integrations/klar/events"
import {
  klaviyoTrackAddedToCart,
  klaviyoTrackStartedCheckout,
  klaviyoTrackViewedProduct,
} from "src/integrations/klaviyo/events"
import {
  mapCartChangeToKlaviyoAddedToCartEvent,
  mapCartToKlaviyoStartedCheckoutEvent,
  mapProductToKlaviyoViewedProductEvent,
} from "src/integrations/klaviyo/mapping"
import { updateUserData } from "src/integrations/lib/userData"
import {
  metaTrackAddPaymentInfo,
  metaTrackAddToCart,
  metaTrackInitiateCheckout,
  metaTrackLead,
  metaTrackPageView,
  metaTrackPurchase,
  metaTrackSearch,
  metaTrackViewContent,
} from "src/integrations/meta/events"
import {
  mapCartOrderToMetaAddPaymentInfoEvent,
  mapCartToMetaInitiateCheckoutEvent,
  mapLineItemToMetaAddToCartEvent,
  mapOrderToMetaPurchaseEvent,
  mapProductToMetaViewContentEvent,
} from "src/integrations/meta/mapping"
import {
  posthogCapture,
  posthogTrackCheckoutStepViewed,
  posthogTrackCouponApplied,
  posthogTrackOrderCompleted,
  posthogTrackPageView,
  posthogTrackProductAdded,
  posthogTrackProductsSearched,
} from "src/integrations/posthog/events"
import {
  mapCartChangeToTrackingLineItem,
  mapCartToTrackingCart,
  mapListProductToTrackingProduct,
  mapOrderToTrackingOrder,
  mapResolvedListProductToTrackingProduct,
} from "src/tracking/mapping"
import { createCartHash } from "src/utilities/cart"
import { isBrowser } from "src/utilities/env"
import { generateRandomId } from "src/utilities/random"
import { useCartState } from "../components/checkout/hooks/useCart"
import { getOrder } from "../components/checkout/state/checkoutOrderStore"
import { CheckoutStep } from "../components/checkout/state/checkoutStageStore"
import { ListProductData } from "../data/product-list/types"

export function generateTrackingEventId() {
  return generateRandomId()
}

async function trackPageView({ referrer, pathname }: { referrer: string; pathname: string }) {
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackPageView({ event_id, referrer })
  metaTrackPageView({ event_id })
  posthogTrackPageView({ referrer, pathname })
}

// Use a non-hook variable to avoid duplicate page view tracking caused by local
// React Strict Mode.
let previousPathname = ""
let previousHref = ""

export function useTrackPageViews() {
  const track = useEventCallback(() => {
    if (!isBrowser) {
      return
    }

    // Avoid tracking duplicate page views
    if (pathname === previousPathname) {
      return
    }

    const referrer = previousPathname ? previousHref : document.referrer

    // ! Track page views outside of an effect so they are tracked before anything
    // else that could be called in an effect.
    trackPageView({ referrer, pathname })

    previousPathname = pathname
    previousHref = window.location.href
  })

  const pathname = useWindowLocationPathname()

  if (hasUserInteractedWithConsent()) {
    track()
  }

  useWindowEvent(Ccm19Event.WidgetLoaded, track)
  useWindowEvent(Ccm19Event.EmbeddingAccepted, track)
  useWindowEvent(Ccm19Event.WidgetClosed, track)
}

export function useTrackViewItemList(listId: string, products: ListProductData[]) {
  return useInViewEffectOncePerPage({
    id: `useTrackViewItemList | ${listId}`,
    effect: () => {
      const trackingProducts = products.map(mapListProductToTrackingProduct)
      const event_id = generateTrackingEventId()

      googleTrackViewItemList({
        event_id,
        item_list_id: listId,
        items: trackingProducts.map(mapProductToGoogleItemData),
      })
    },
  })
}

export async function trackSearch({ listId, input }: { listId: string; input: string }) {
  const inputCleaned = input.trim().toLowerCase()

  if (!inputCleaned) {
    return
  }

  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackSearch({ event_id, search_term: inputCleaned })
  metaTrackSearch({ event_id, search_string: inputCleaned })
  posthogTrackProductsSearched({ listId, input: inputCleaned })
}

export async function trackProductLinkClick(listId: string, product: ListProductData) {
  const trackingProduct = mapListProductToTrackingProduct(product)
  const item = mapProductToGoogleItemData(trackingProduct)
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackSelectItem({ event_id, item_list_id: listId, items: [item] })
  posthogCapture("product_link_clicked", { listId, sku: product.sku })

  if (trackingProduct.promotionType) {
    const promotionItem = mapProductToGoogleViewPromotionItemData(trackingProduct)

    googleTrackSelectPromotion({ event_id, items: [promotionItem] })
  }
}

export async function trackViewEmbed(data: { integrationId: string; embedId: string }) {
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackEmbedView({ event_id, ...data })
}

export async function trackEmailSignup(data: { listId: string; name: string; context: string }) {
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackGenerateLead({ event_id, currency: undefined, value: undefined, lead_source: data.listId })
  metaTrackLead({ event_id, currency: undefined, value: undefined })
  posthogCapture("signup", { name: data.name, list_id: data.listId, context: data.context })
}

export async function trackWhatsAppLinkViewed(href: string) {
  posthogCapture("whatsapp_link_viewed", { href })
}

export function useTrackWhatsAppLinkViewed(href: string) {
  const ref = useRef<HTMLElement>(null)
  const [inView] = useInViewport(ref, { threshold: 1 })
  const trackedRef = useRef(false)

  useEffect(() => {
    if (inView && !trackedRef.current) {
      trackedRef.current = true
      trackWhatsAppLinkViewed(href)
    }
  }, [inView, href])

  return ref
}

export async function trackWhatsAppLinkClicked(href: string) {
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackGenerateLead({ event_id, currency: undefined, value: undefined, lead_source: href })
  dataLayerPushEvent("whatsapp_link_clicked", { event_id, href }) // custom conversion event for retargeting ads
  posthogCapture("whatsapp_link_clicked", { href })
}

async function trackViewPromotion(product: ListProductData) {
  const trackingProduct = mapListProductToTrackingProduct(product)

  if (trackingProduct.promotionType) {
    const promotionItem = mapProductToGoogleViewPromotionItemData(trackingProduct)
    const event_id = generateTrackingEventId()

    await updateUserData()
    googleTrackViewPromotion({ event_id, items: [promotionItem] })
  }
}

export function useTrackViewPromotion(product: ListProductData) {
  return useInViewEffectOncePerPage({
    id: `useTrackViewPromotion | ${product.id}`,
    effect: () => trackViewPromotion(product),
  })
}

async function trackViewProduct(product: ResolvedListProductData) {
  const trackingProduct = mapResolvedListProductToTrackingProduct(product)
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackViewItem(mapProductToGoogleViewItemEvent(event_id, trackingProduct))
  metaTrackViewContent(mapProductToMetaViewContentEvent(event_id, trackingProduct))
  klaviyoTrackViewedProduct(mapProductToKlaviyoViewedProductEvent(trackingProduct))
  posthogCapture("promotion_viewed", trackingProduct)

  if (trackingProduct.promotionType) {
    const promotionItem = mapProductToGoogleViewPromotionItemData(trackingProduct)

    googleTrackViewPromotion({ event_id, items: [promotionItem] })
  }
}

export function useTrackViewProduct(product: ResolvedListProductData) {
  return useInViewEffectOncePerPage({
    id: `useTrackViewProduct | ${product.id}`,
    effect: () => trackViewProduct(product),
  })
}

export function useTrackViewDetailProduct(resolvedProductData: ResolvedProductData) {
  return useTrackViewProduct({
    id: resolvedProductData.resolvedId,
    slug: resolvedProductData.slug,
    sku: resolvedProductData.resolvedSku,
    name: resolvedProductData.name,
    categories: resolvedProductData.categories,
    image: resolvedProductData.resolvedFirstImage,
    slogan: resolvedProductData.slogan,
    fulfillmentFrom: resolvedProductData.resolvedFulfillmentFrom,
    freeShipping: resolvedProductData.resolvedFreeShipping,
    price: resolvedProductData.resolvedPrice,
    regularPrice: resolvedProductData.resolvedRegularPrice,
    discount: resolvedProductData.resolvedDiscount,
    coupon: resolvedProductData.resolvedCoupon,
    available: resolvedProductData.resolvedAvailable,
    rating: resolvedProductData.rating,
    variationType: resolvedProductData.variationType,
    variationName: resolvedProductData.variation?.name,
    colors: resolvedProductData.colors,
    accessorySlugs: undefined, // not relevant for tracking
    parents: undefined, // not relevant for tracking
  } satisfies ResolvedListProductData)
}

export async function trackAddToCart(cart: StorefrontCart, item: StorefrontCartItem, quantityChange: number) {
  const trackingCart = mapCartToTrackingCart(cart)
  const trackingLineItem = mapCartChangeToTrackingLineItem(cart, item, quantityChange)
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackAddToCart(mapLineItemToGoogleAddToCartEvent(event_id, trackingLineItem))
  metaTrackAddToCart(mapLineItemToMetaAddToCartEvent(event_id, trackingLineItem))
  klaviyoTrackAddedToCart(mapCartChangeToKlaviyoAddedToCartEvent(trackingCart, trackingLineItem, quantityChange))
  posthogTrackProductAdded(trackingLineItem)
  klarTrackCartEvent()
}

export async function trackRemoveFromToCart(cart: StorefrontCart, item: StorefrontCartItem, quantityChange: number) {
  const trackingCart = mapCartToTrackingCart(cart)
  const trackingLineItem = mapCartChangeToTrackingLineItem(cart, item, quantityChange)
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackRemoveFromCart(mapProductToGoogleRemoveFromCartEvent(event_id, trackingLineItem))
  klaviyoTrackAddedToCart(mapCartChangeToKlaviyoAddedToCartEvent(trackingCart, trackingLineItem, -quantityChange))
  klarTrackCartEvent()
}

async function trackViewCart(cart: StorefrontCart) {
  const trackingCart = mapCartToTrackingCart(cart)
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackViewCart(mapCartToGoogleViewCartEvent(event_id, trackingCart))
}

export async function trackCheckoutStep(step: CheckoutStep) {
  await updateUserData()
  posthogTrackCheckoutStepViewed(step)
}

export function useTrackViewCart() {
  const cart = useCartState().data
  const cartHashRef = useRef("")

  const cartHash = createCartHash(cart)

  useEffect(() => {
    if (!cart || cartHash === cartHashRef.current) {
      return
    }

    trackViewCart(cart)

    cartHashRef.current = cartHash
  }, [cart, cartHash])
}

export async function trackCouponApplied(code: string) {
  const codeLower = code.toLowerCase()

  await updateUserData()
  posthogTrackCouponApplied({ code: codeLower })
  klarTrackCartEvent()
}

export async function trackBeginCheckout(cart: StorefrontCart) {
  const trackingCart = mapCartToTrackingCart(cart)
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackBeginCheckout(mapCartToGoogleBeginCheckoutEvent(event_id, trackingCart))
  metaTrackInitiateCheckout(mapCartToMetaInitiateCheckoutEvent(event_id, trackingCart))
  klaviyoTrackStartedCheckout(mapCartToKlaviyoStartedCheckoutEvent(trackingCart))
  klarTrackCheckoutEvent()
}

export async function trackAddShippingInfo(cart: StorefrontCart) {
  const trackingCart = mapCartToTrackingCart(cart)
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackAddShippingInfo(mapCartToGoogleAddShippingInfoEvent(event_id, trackingCart))
  klarTrackCheckoutEvent()
}

export async function trackAddPaymentInfo(cart: StorefrontCart, order: WCStoreCreateOrderResponseData) {
  const trackingCart = mapCartToTrackingCart(cart, order.payment_method)
  const event_id = generateTrackingEventId()

  await updateUserData()
  googleTrackAddPaymentInfo(mapCartOrderToGoogleAddPaymentInfoEvent(event_id, trackingCart))
  metaTrackAddPaymentInfo(mapCartOrderToMetaAddPaymentInfoEvent(event_id, trackingCart))
  klarTrackCheckoutEvent()
}

export function isOrderSucecssful(order: StorefrontOrder | undefined) {
  if (!order) {
    return false
  }

  const status = order.status

  return status === "completed" || status === "processing" || status === "pending" || status === "on-hold"
}

export async function trackPurchase() {
  const order = getOrder()

  if (!order || !isOrderSucecssful(order)) {
    return
  }

  const trackingOrder = mapOrderToTrackingOrder(order)
  // Here, the event_id is the order ID.

  await updateUserData()
  googleTrackPurchase(mapOrderToGooglePurchaseEvent(trackingOrder))
  metaTrackPurchase(mapOrderToMetaPurchaseEvent(trackingOrder))
  posthogTrackOrderCompleted(trackingOrder)
  klarTrackOrderEvent(trackingOrder)
}
